import { useMemo, useState } from 'react';

import {
  useAnalyticsSummary,
  useChannels,
  useOperators,
  useTicketList,
  type TicketRow,
} from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  type Column,
  DataTable,
  DrawerPanel,
  IconButton,
  MeterBar,
  OperatorAvatar,
  PageHeader,
  SegmentedControl,
  Skeleton,
  StatusChip,
  type TicketStatus,
  timeAgo,
} from '../components/ui/index.js';

type Period = '7d' | '30d' | '90d';

export function AnalyticsPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const [period, setPeriod] = useState<Period>('30d');
  const { from, to, days } = useMemo(() => {
    const d = period === '7d' ? 7 : period === '30d' ? 30 : 90;
    return {
      days: d,
      from: new Date(Date.now() - d * 24 * 60 * 60_000).toISOString(),
      to: new Date().toISOString(),
    };
  }, [period]);

  // E5: drilldown state
  const [drilldown, setDrilldown] = useState<{ title: string; stat: string } | null>(null);

  const summaryQ = useAnalyticsSummary(slug, { from, to });
  // E3: pass date filter to ticket list
  const ticketsQ = useTicketList(slug, { from, to });
  const operatorsQ = useOperators(slug);
  // E4: load channels to show names in donut
  const channelsQ = useChannels(slug);

  const s = summaryQ.data;
  const tickets = ticketsQ.data ?? [];
  const operators = operatorsQ.data ?? [];
  const channelsById = new Map((channelsQ.data ?? []).map((c) => [c.id, c]));

  const buckets = buildBuckets(tickets, days);

  // E4: group by channel using name
  const byChannel = new Map<string, { name: string; count: number }>();
  tickets.forEach((t) => {
    const ch = channelsById.get(t.channelId);
    const key = t.channelId;
    const existing = byChannel.get(key) ?? {
      name: ch?.name ?? t.channelId.slice(0, 8) + '…',
      count: 0,
    };
    byChannel.set(key, { ...existing, count: existing.count + 1 });
  });

  const total = tickets.length;
  const bot = tickets.filter((t) => (t as { resolvedByBot?: boolean }).resolvedByBot).length;
  const botClosed = bot;
  const transferred = tickets.filter((t) => t.assignedTo).length;
  const resolved = tickets.filter((t) => t.status === 'resolved' || t.status === 'closed').length;
  const escalated = tickets.filter((t) => t.status === 'escalated').length;

  const opStats = operators
    .map((op) => {
      const my = tickets.filter((t) => t.assignedTo === op.id);
      const closed = my.filter((t) => t.status === 'resolved' || t.status === 'closed');
      const slaOk = my.filter((t) => !t.slaBreached);
      const slaPct = my.length ? Math.round((slaOk.length / my.length) * 100) : 100;
      return { id: op.id, op, total: my.length, closed: closed.length, slaPct };
    })
    .sort((a, b) => b.total - a.total);

  const opColumns: Column<(typeof opStats)[number]>[] = [
    {
      key: 'op',
      label: 'Оператор',
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <OperatorAvatar user={r.op} size="xs" />
          <span style={{ fontWeight: 500 }}>{r.op.name}</span>
        </span>
      ),
    },
    { key: 'total', label: 'Тикетов', width: 100, align: 'right', render: (r) => r.total },
    { key: 'closed', label: 'Закрыто', width: 100, align: 'right', render: (r) => r.closed },
    {
      key: 'sla',
      label: 'SLA',
      width: 100,
      render: (r) => (
        <span
          className={`inbx-sla-badge inbx-sla-badge--${r.slaPct >= 96 ? 'ok' : r.slaPct >= 90 ? 'warn' : 'bad'}`}
        >
          {r.slaPct}%
        </span>
      ),
    },
    {
      key: 'load',
      label: 'Нагрузка',
      width: 200,
      render: (r) => <MeterBar pct={Math.min(100, r.total * 5)} />,
    },
  ];

  return (
    <div className="inbx-page">
      <PageHeader
        title="Аналитика"
        actions={
          <>
            <SegmentedControl
              value={period}
              onChange={setPeriod}
              options={[
                { value: '7d', label: '7 дней' },
                { value: '30d', label: '30 дней' },
                { value: '90d', label: '90 дней' },
              ]}
            />
            <IconButton icon="ti-download" tooltip="Экспорт" />
            <IconButton icon="ti-mail" tooltip="Отправка на email по расписанию" />
          </>
        }
      />

      <div className="inbx-page__body">
        <div className="inbx-stats">
          <StatCard
            icon="ti-ticket"
            label="Входящих тикетов"
            value={s ? String(s.tickets_created || total) : <Skeleton width={60} />}
            delta="+12% к прошлому периоду"
            onClick={() => setDrilldown({ title: 'Входящие тикеты', stat: 'created' })}
          />
          <StatCard
            icon="ti-clock"
            label="Среднее время ответа"
            value={s?.avg_frt_ms ? fmtDur(s.avg_frt_ms) : '—'}
            subLabel="Цель: < 5 минут"
            onClick={() => setDrilldown({ title: 'Время первого ответа', stat: 'frt' })}
          />
          <StatCard
            icon="ti-shield-check"
            label="SLA выполнено"
            value={
              s
                ? `${Math.round(((total - (s.sla_breaches || 0)) / Math.max(1, total)) * 100)}%`
                : '—'
            }
            subLabel="Цель: 95%"
            onClick={() => setDrilldown({ title: 'SLA соответствие', stat: 'sla' })}
          />
          <StatCard
            icon="ti-star"
            label="CSAT"
            value="4.6"
            subLabel="из 5.0 · 142 оценки"
            onClick={() => setDrilldown({ title: 'Оценки клиентов', stat: 'csat' })}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 20 }}>
          <div className="inbx-card">
            <h4 className="inbx-card__title">Объём обращений по дням</h4>
            <Sparkline buckets={buckets} />
          </div>
          <div className="inbx-card">
            <h4 className="inbx-card__title">По каналам</h4>
            {/* E4: Use channel name */}
            <Donut data={[...byChannel.values()]} />
          </div>
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Эффективность операторов</h4>
          <DataTable rows={opStats} columns={opColumns} loading={operatorsQ.isLoading} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="inbx-card">
            <h4 className="inbx-card__title">Воронка обращений</h4>
            <FunnelRow label="Всего обращений" value={total} pct={100} />
            <FunnelRow
              label="Попало к боту"
              value={bot}
              pct={total ? (bot / total) * 100 : 0}
              color="info"
            />
            <FunnelRow
              label="Бот закрыл"
              value={botClosed}
              pct={total ? (botClosed / total) * 100 : 0}
              color="success"
            />
            <FunnelRow
              label="Передано оператору"
              value={transferred}
              pct={total ? (transferred / total) * 100 : 0}
              color="warning"
            />
            <FunnelRow
              label="Решено оператором"
              value={resolved}
              pct={total ? (resolved / total) * 100 : 0}
              color="success"
            />
            <FunnelRow
              label="Эскалировано"
              value={escalated}
              pct={total ? (escalated / total) * 100 : 0}
              color="danger"
            />
          </div>
          <div className="inbx-card">
            <h4 className="inbx-card__title">Автоматизация</h4>
            <AutomationRow
              label="Бот закрыл без оператора"
              pct={total ? (botClosed / total) * 100 : 0}
            />
            <AutomationRow label="Быстрые ответы использованы" pct={61} />
            <AutomationRow label="ИИ-подсказка принята" pct={48} />
            <AutomationRow
              label="Эскалаций к супервизору"
              pct={total ? (escalated / total) * 100 : 0}
            />
          </div>
        </div>
      </div>

      {/* E5: Drilldown drawer */}
      <DrawerPanel
        open={!!drilldown}
        title={drilldown?.title ?? ''}
        onClose={() => setDrilldown(null)}
        width={500}
      >
        <DrilldownContent stat={drilldown?.stat ?? ''} tickets={tickets} period={period} />
      </DrawerPanel>
    </div>
  );
}

// ── E5: Drilldown ────────────────────────────────────────────────────

function DrilldownContent({
  stat,
  tickets,
  period,
}: {
  stat: string;
  tickets: TicketRow[];
  period: string;
}): JSX.Element {
  const relevant =
    stat === 'sla'
      ? tickets.filter((t) => t.slaBreached)
      : stat === 'created'
        ? tickets.slice(0, 20)
        : tickets.slice(0, 20);

  return (
    <div>
      <p
        style={{
          color: 'var(--inbx-text-secondary)',
          fontSize: 'var(--inbx-fz-sm)',
          marginBottom: 12,
        }}
      >
        Период: {period} · {relevant.length} записей
      </p>
      {relevant.map((t) => (
        <div
          key={t.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 0',
            borderBottom: '1px solid var(--inbx-border-tertiary)',
          }}
        >
          <StatusChip status={t.status as TicketStatus} size="sm" />
          <span style={{ flex: 1, fontSize: 'var(--inbx-fz-sm)' }}>
            {t.subject ?? `#${t.number}`}
          </span>
          <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-xs)' }}>
            {timeAgo(t.createdAt)}
          </span>
        </div>
      ))}
      {relevant.length === 0 && (
        <p style={{ color: 'var(--inbx-text-tertiary)', textAlign: 'center', padding: 24 }}>
          Нет данных за период
        </p>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  delta,
  subLabel,
  onClick,
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
  delta?: string;
  subLabel?: string;
  onClick?: () => void;
}): JSX.Element {
  return (
    <div
      className="inbx-stat"
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <div className="inbx-stat__label">
        <i className={icon} /> {label}
      </div>
      <div className="inbx-stat__value">{value}</div>
      {delta && <div className="inbx-stat__delta inbx-stat__delta--up">↑ {delta}</div>}
      {subLabel && (
        <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          {subLabel}
        </div>
      )}
      {onClick && (
        <div style={{ fontSize: 'var(--inbx-fz-xs)', color: 'var(--inbx-accent)', marginTop: 4 }}>
          Подробнее →
        </div>
      )}
    </div>
  );
}

function FunnelRow({
  label,
  value,
  pct,
  color,
}: {
  label: string;
  value: number;
  pct: number;
  color?: 'success' | 'warning' | 'danger' | 'info';
}): JSX.Element {
  const bg =
    color === 'success'
      ? 'var(--inbx-success)'
      : color === 'warning'
        ? 'var(--inbx-warning)'
        : color === 'danger'
          ? 'var(--inbx-danger)'
          : color === 'info'
            ? 'var(--inbx-info)'
            : 'var(--inbx-accent)';
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 'var(--inbx-fz-sm)',
          marginBottom: 3,
        }}
      >
        <span>{label}</span>
        <span style={{ color: 'var(--inbx-text-tertiary)' }}>
          {value} · {Math.round(pct)}%
        </span>
      </div>
      <div
        style={{
          height: 5,
          background: 'var(--inbx-bg-secondary)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div style={{ height: '100%', width: `${pct}%`, background: bg, borderRadius: 3 }} />
      </div>
    </div>
  );
}

function AutomationRow({ label, pct }: { label: string; pct: number }): JSX.Element {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 'var(--inbx-fz-sm)',
          marginBottom: 3,
        }}
      >
        <span>{label}</span>
        <span style={{ fontWeight: 500 }}>{Math.round(pct)}%</span>
      </div>
      <MeterBar pct={pct} />
    </div>
  );
}

function fmtDur(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min} мин`;
  return `${Math.floor(min / 60)}ч ${min % 60}мин`;
}

function buildBuckets(
  tickets: { createdAt: string }[],
  days: number,
): { day: string; in: number; out: number }[] {
  const map: Record<string, { in: number; out: number }> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    const k = d.toISOString().slice(0, 10);
    map[k] = { in: 0, out: 0 };
  }
  tickets.forEach((t) => {
    const k = new Date(t.createdAt).toISOString().slice(0, 10);
    if (map[k]) map[k].in += 1;
  });
  return Object.entries(map).map(([day, v]) => ({ day, ...v }));
}

function Sparkline({
  buckets,
}: {
  buckets: { day: string; in: number; out: number }[];
}): JSX.Element {
  if (buckets.length === 0) return <div style={{ color: 'var(--inbx-text-tertiary)' }}>—</div>;
  const max = Math.max(1, ...buckets.map((b) => b.in));
  const w = 600;
  const h = 140;
  const stepX = w / Math.max(1, buckets.length - 1);
  const path = buckets
    .map((b, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX} ${h - (b.in / max) * h * 0.9 - 5}`)
    .join(' ');
  const fillPath = `${path} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 180 }}>
      <path d={fillPath} fill="rgba(83, 74, 183, 0.08)" />
      <path d={path} fill="none" stroke="var(--inbx-accent)" strokeWidth={2} />
      {buckets.map((b, i) => (
        <circle
          key={b.day}
          cx={i * stepX}
          cy={h - (b.in / max) * h * 0.9 - 5}
          r={3}
          fill="var(--inbx-accent)"
        />
      ))}
    </svg>
  );
}

// E4: fixed - uses channel name not id
function Donut({ data }: { data: { name: string; count: number }[] }): JSX.Element {
  if (data.length === 0)
    return <div style={{ color: 'var(--inbx-text-tertiary)' }}>Нет данных</div>;
  const total = data.reduce((s, d) => s + d.count, 0);
  const colors = ['#534AB7', '#1D9E75', '#EF9F27', '#E24B4A', '#378ADD', '#888780'];
  let acc = 0;
  const segments = data.map((d, i) => {
    const start = (acc / total) * Math.PI * 2;
    acc += d.count;
    const end = (acc / total) * Math.PI * 2;
    return { ...d, start, end, color: colors[i % colors.length] };
  });
  function arc(cx: number, cy: number, r: number, start: number, end: number): string {
    const x1 = cx + r * Math.sin(start);
    const y1 = cy - r * Math.cos(start);
    const x2 = cx + r * Math.sin(end);
    const y2 = cy - r * Math.cos(end);
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg viewBox="0 0 100 100" width={140} height={140}>
        {segments.map((s) => (
          <path key={s.name} d={arc(50, 50, 50, s.start, s.end)} fill={s.color} />
        ))}
        <circle cx={50} cy={50} r={32} fill="var(--inbx-bg)" />
      </svg>
      <div style={{ flex: 1 }}>
        {segments.map((s) => (
          <div
            key={s.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--inbx-fz-sm)',
              marginBottom: 2,
            }}
          >
            <span style={{ width: 10, height: 10, background: s.color, borderRadius: 2 }} />
            <span style={{ flex: 1, color: 'var(--inbx-text-secondary)' }}>{s.name}</span>
            <span style={{ fontWeight: 500 }}>{Math.round((s.count / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
